import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { graphql, Link, useStaticQuery } from 'gatsby';
import { Container, Dropdown, Icon, Image, List } from '@pella/sash-framework';
import { CTA } from '../../';

import { SupportMenu, ColumnsMenu, ProductsMenu } from './menu-types';
import MobileMenu from '../mobile-nav';

import './index.css';

const Header = ({ showSchedule = true }) => {
	const staticData = useStaticQuery(query).contentstackPalHeader;
	const scrollYOffset = useRef(0);
	const [showMobileNav, setShowMobileNav] = useState(false);

	useEffect(() => {
		window.addEventListener('scroll', onPageScroll, { passive: true });
		return () => window.removeEventListener('scroll', onPageScroll);
	}, []);

	const onMobileBtnClick = () => setShowMobileNav(!showMobileNav);

	const onPageScroll = () => {
		const siteDiv = document.body;

		// Remove updates from scrolling
		if (window.scrollY === 0 || (window.scrollY <= 150 && window.innerWidth > 1030)) {
			siteDiv?.classList.remove('isScrollingDown');
			siteDiv?.classList.remove('isScrollingUp');
		}

		// Trigger the desktop slimmer menu
		if (window.scrollY > 150 && window.scrollY > scrollYOffset.current) {
			siteDiv?.classList.remove('isScrollingUp');
			siteDiv?.classList.add('isScrollingDown');
		}

		// Trigger the re-appearance in mobile on scroll up
		if (window.scrollY > 150 && window.scrollY < scrollYOffset.current) {
			siteDiv?.classList.remove('isScrollingDown');
			siteDiv?.classList.add('isScrollingUp');
		}

		scrollYOffset.current = window.scrollY;
	};

	return (
		<>
			<header className={`pal-header fixed left-0 z-[20] w-full bg-white max-sm:bottom-0 max-sm:border-t-[5px] max-sm:border-t-secondary sm:top-0 sm:border-b-[5px] sm:border-b-secondary xl:py-2.5 [&_a]:text-black ${showMobileNav ? '!translate-y-0' : ''}`}>
				<Container
					className='flex flex-wrap items-center max-xl:!pr-0'
					width={1920}
				>
					{staticData?.home_icon?.url && (
						<Link
							to='/'
							title='Home'
							className='pal-logo'
						>
							<Image
								{...staticData.home_icon.gatsbyImageData.images.fallback}
								alt='Pella at Lowes'
								loading='eager'
								className='block h-[30px] md:h-[35px] 2xl:h-[45px]'
							/>
						</Link>
					)}

					{staticData?.group?.[0] && (
						<nav
							aria-label='Desktop Nav'
							className='pal-navigation hidden flex-1 flex-wrap items-center self-end xl:flex xl:pl-9 2xl:pl-14'
						>
							{staticData.group.map((item, index) => (
								<div
									className='pal-navItem'
									key={index}
								>
									{item.section?.[0] && (
										<Dropdown
											className='group/dd !static'
											backdropClass='bg-black/40 top-[calc(100%+5px)] absolute left-0 w-screen h-screen backdrop-blur-lg -z-1'
											hasBackdrop
										>
											<Dropdown.Trigger
												className='pal-navBtn inline-flex items-center border-0 bg-transparent pb-1 pt-4 text-black xl:px-2.5 2xl:px-5 [&_svg]:ml-0.5 [&_svg]:text-2xl'
												activeClass='[&_span]:decoration-primary'
											>
												<span className='font-semibold underline decoration-transparent decoration-2 underline-offset-4 group-focus-within/dd:decoration-primary group-hover/dd:decoration-primary xl:text-[17px] 2xl:text-lg '>{item.heading}</span>
												<Icon
													name='keyboard_arrow_down'
													className='lg:ml-1 xl:ml-1.5'
												/>
											</Dropdown.Trigger>

											<Dropdown.Content className='z-[20] mt-[5px] w-full border-0 !px-0 !py-5'>
												<Container>
													{item.heading === 'Products' && <ProductsMenu data={item.section} />}
													{item.heading === 'Getting Started' && <ColumnsMenu data={item.section[0]} />}
													{item.heading?.includes('Support') && <SupportMenu data={item.section[0]} />}
												</Container>
											</Dropdown.Content>
										</Dropdown>
									)}

									{!item.section?.[0] && item.linkable_url && (
										<Link
											to={item.linkable_url}
											className='pal-navBtn inline-flex pb-1 pt-4 font-semibold text-black decoration-transparent decoration-2 underline-offset-4 focus-within:decoration-primary hover:decoration-primary xl:px-2.5 xl:text-[17px] 2xl:px-5 2xl:text-lg'
										>
											{item.heading}
										</Link>
									)}
								</div>
							))}
						</nav>
					)}

					<List
						bullets='none'
						layout='inline'
						className='pal-global ml-auto flex-none flex-row items-center divide-x divide-gray-15'
					>
						{showSchedule &&
							staticData?.global?.[0] &&
							staticData.global.map((item, index) => (
								<li
									key={index}
									className='leading-snug'
								>
									<CTA
										data={item}
										size='sm'
										className='mr-4 hidden py-2.5 normal-case sm:block xl:mr-0 xl:px-6 2xl:px-9'
									/>
								</li>
							))}

						<li className='leading-snug xl:hidden'>
							<a
								href='https://www.sellpellaatlowes.com'
								target='_blank'
								rel='noopener noreferrer'
								title='Link opens Sell Pella at Lowes in a new window'
								className='flex h-[60px] w-[60px] flex-col items-center justify-center no-underline'
							>
								<Icon
									name='account_circle'
									className='mx-auto block'
									fill={1}
								/>
								<span className='mt-0.5 block text-xs'>Login</span>
							</a>
						</li>

						<li className='leading-snug xl:hidden'>
							<button
								className='flex h-[60px] w-[60px] flex-col items-center justify-center border-0 bg-transparent no-underline'
								onClick={onMobileBtnClick}
							>
								<Icon
									name={showMobileNav ? 'close' : 'menu'}
									className='mx-auto block'
								/>
								<span className='mt-0.5 block text-xs'>Menu</span>
							</button>
						</li>
					</List>
				</Container>
			</header>

			<MobileMenu
				data={staticData}
				isOpen={showMobileNav}
			/>

			{showMobileNav && (
				<button
					aria-label='Nav Backdrop'
					onClick={onMobileBtnClick}
					className='mobileMenu-overlay fixed left-0 top-0 z-1 h-screen w-screen bg-black/40 backdrop-blur-lg xl:hidden'
				/>
			)}
		</>
	);
};

Header.propTypes = {
	showSchedule: PropTypes.bool,
};

export default Header;

export const query = graphql`
	{
		contentstackPalHeader {
			home_icon {
				url
				gatsbyImageData(quality: 90)
			}
			global {
				...CTA
			}
			group {
				heading
				linkable_url
				section {
					section_heading {
						...CTA
					}
					subnav {
						file {
							url
							gatsbyImageData(quality: 90)
						}
						heading
						ctas {
							...CTA
						}
					}
				}
			}
		}
	}
`;
